import React from "react";
import { Gift, Check } from "lucide-react";

// 🖼️ When the user uploads the mask photos, swap these URLs.
//   Recommended: 600×600 px JPEG, transparent or clean studio background.
const MASK_COLORS = [
  {
    key: "Black",
    label: "Black",
    swatch: "#0e0e10",
    image: "/assets/mask_black.jpg",
  },
  {
    key: "Pink",
    label: "Pink",
    swatch: "#f4b8c8",
    image: "/assets/mask_pink.jpg",
  },
];

/**
 * Free 3D sleep-mask color picker — shown when the customer selects the 2-pillow
 * (1 free mask) or 4-pillow (2 free masks) bundle.
 *
 * Props:
 *   count            — number of mask slots (1 for bundle=2, 2 for bundle=4)
 *   selectedColors   — Array<string> currently picked, length = count
 *   onChange         — (index, color) => void
 */
export default function GiftMaskSelector({ count, selectedColors, onChange }) {
  if (!count) return null;

  return (
    <div
      data-testid="gift-mask-selector"
      className="mt-4 rounded-2xl border-2 border-[#d8f06b]/60 bg-gradient-to-r from-[#f9ffd9] via-white to-[#fffaf0] p-4 md:p-5"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-[#0e0e10] bg-[#d8f06b] px-2 py-0.5 rounded-full inline-flex items-center gap-1">
          <Gift size={11} /> Free gift
        </span>
        <span className="text-[10px] uppercase tracking-wider font-bold text-[#22c55e]">
          $39 value · included
        </span>
      </div>

      <div className="text-[14px] font-black text-[#0e0e10] leading-tight">
        {count === 1
          ? "Pick the color of your free 3D sleep mask"
          : `Pick ${count} colors for your free 3D sleep masks`}
      </div>
      <p className="mt-1 text-[11px] text-[#5a5a60] leading-snug">
        Premium 3D contoured sleep mask · 100% blackout · zero pressure on the eyes · ultra-light travel cover for deep rest.
      </p>

      <div className="mt-3 space-y-3">
        {Array.from({ length: count }).map((_, slot) => (
          <div key={slot} className="space-y-1.5">
            {count > 1 && (
              <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#8a8a8a]">
                Mask {slot + 1}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2.5">
              {MASK_COLORS.map((c) => {
                const isSelected = selectedColors[slot] === c.key;
                return (
                  <button
                    key={`${slot}-${c.key}`}
                    type="button"
                    onClick={() => onChange(slot, c.key)}
                    data-testid={`mask-${slot}-${c.key.toLowerCase()}`}
                    aria-pressed={isSelected}
                    className={`relative flex items-center gap-2.5 rounded-xl border-2 px-2.5 py-2 transition ${
                      isSelected
                        ? "border-[#0e0e10] bg-white shadow-md"
                        : "border-black/10 bg-white hover:border-[#0e0e10]/40"
                    }`}
                  >
                    <span
                      className="w-12 h-12 rounded-lg overflow-hidden border border-black/10 shrink-0 bg-[#fafaf9]"
                      aria-hidden
                    >
                      <img
                        src={c.image}
                        alt={`${c.label} 3D sleep mask`}
                        loading="lazy"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.background = c.swatch; e.currentTarget.style.display = "none"; }}
                      />
                    </span>
                    <span className="text-[12px] font-black text-[#0e0e10] uppercase tracking-wider">
                      {c.label}
                    </span>
                    {isSelected && (
                      <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-[#0e0e10] text-white inline-flex items-center justify-center">
                        <Check size={11} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
